import { Box, Checkbox, Chip, Grid, IconButton, MenuItem, Paper, Select, TextField, Tooltip, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/AddCircle';
import CloseIcon from '@material-ui/icons/Close';
import React, { useState } from 'react';
import { IBundlePackage, IBundlePrice, IStory } from '../../services/types';

interface IProps {
  story: IStory;
  bundle: IBundlePackage;
  title?: string;
  isEditing?: boolean;
  removable?: boolean;
  onChange: (bundle: IBundlePackage) => void;
  onRemoveClick?: (bundle: IBundlePackage) => void;
  onAddDynamicBundleClick?: (bundle: IBundlePackage) => void;
  isDynamicBundle?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  item: {
    paddingTop: theme.spacing(2.25),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(2.25),
    width: 560,
  },
  tier: {
    height: 32,
  },
  number: {
    marginRight: 8,
  },
  remainEpisodeTitle: {
    marginRight: 6,
    width: 150,
  },
  remainEpisodeTextField: {
    width: 60,
    marginLeft: 4,
    marginRight: 4,
  },
  errorMessage: {
    color: '#f00',
  },
  bundleIdChip: {
    marginRight: 8,
  },
}));

const SingleBundle = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const {
    story,
    bundle,
    title = '',
    isEditing = false,
    removable = false,
    onChange,
    onRemoveClick = () => {},
    onAddDynamicBundleClick = () => {},
    isDynamicBundle = false,
  } = props;

  const { showRemainAll, prices } = bundle;
  const episodeUnitPrice = story.episodePrice;

  const [minEpisode, setMinEpisode] = useState<string>(String(bundle.minRemainEpisodesNumber || ''));
  const [maxEpisode, setMaxEpisode] = useState<string>(String(bundle.maxRemainEpisodesNumber || ''));
  const [errorMessage, setErrorMessage] = useState<string>();

  const getRatioToPercentage = (ratio: number) => Math.floor(100 - ratio * 100);

  const handleCheckboxChange = () => {
    onChange({ ...bundle, showRemainAll: !showRemainAll });
  };

  const handleAddDynamicBundleClick = () => {
    onAddDynamicBundleClick({ ...bundle });
  };

  const handleRemoveClick = () => {
    if (removable && onRemoveClick) {
      onRemoveClick(bundle);
    }
  };

  const renderEpisodeCountItems = (previousTier: Partial<IBundlePrice> | undefined) => {
    const minimumEpisodeCount = Math.max(story.episodeCount, 300);

    const items = Array.from({ length: minimumEpisodeCount }, (_, n) => {
      const value = n + 1;
      const disabled = !!previousTier?.episodeNum && previousTier.episodeNum >= value;

      return (
        <MenuItem key={value} value={value} disabled={disabled}>
          {value}
        </MenuItem>
      );
    });

    const unselect = (
      <MenuItem key={'---'} value={undefined}>
        ---
      </MenuItem>
    );

    return [unselect, ...items];
  };

  const renderCalculatedCoinItems = (episodeCount?: number, previousTier?: Partial<IBundlePrice>) => {
    if (!episodeCount) {
      return null;
    }

    /*
      - 에피소드 1개 단위로 설정할 수 있도록, 할인율은 1% 단위로 설정할 수 있도록 함.
      - 적용된 할인율로 계산한 코인 갯수가 동일한 경우는 메뉴에서 나타나지 않도록 중복을 제거 함.
      - 0% 부터 70%까지 제시.
      - 코인수 계산 = Episode Unit Price * Episode Count * (1-할인율)
      - 상위 Tier의 할인율은 하위 Tier의 할인율보다 같거나 큰 값만 선택할 수 있음.
    */
    const distinctCoins = Array.from({ length: 71 }, (_, n) => {
      const discountRatio = 1 - (n > 0 ? n / 100 : 0);
      return Math.round(episodeUnitPrice * episodeCount * discountRatio);
    }).filter((coins, index, arr) => {
      if (index > 0) {
        const previousCoins = arr[index - 1];

        // 할인율은 다르나 할인율이 적용된 코인 갯수가 같은 경우는 목록에서 제거.
        if (previousCoins === coins) {
          return false;
        }
      }

      return coins > 0;
    });

    return distinctCoins.map(coins => {
      const discountRatioPercentage = getRatioToPercentage(coins / (episodeUnitPrice * episodeCount));
      const previousDiscountRatioPercentage =
        previousTier?.price && previousTier?.episodeNum ? getRatioToPercentage(previousTier.price / (episodeUnitPrice * previousTier.episodeNum)) : 0;

      const disabled = previousDiscountRatioPercentage > discountRatioPercentage;

      return (
        <MenuItem key={coins} value={coins} disabled={disabled}>
          {`${coins} (${discountRatioPercentage}% off)`}
        </MenuItem>
      );
    });
  };

  const getLengthOfTiers = (): number => {
    const CurrentMaxTiers = 4;

    if (!isEditing && prices) {
      return prices.length;
    }

    return CurrentMaxTiers;
  };

  const validateRemainEpisodeRange = (onValid: () => void) => {
    const minValue = Number(minEpisode);
    const maxValue = Number(maxEpisode);

    if (minValue >= maxValue) {
      setErrorMessage('the minimum count exceed the maximum count.');
    } else if (minValue <= 0 || maxValue <= 0) {
      setErrorMessage('the count should be a positive value');
    } else {
      setErrorMessage(undefined);
    }

    onValid();
  };

  const handleMinRemainEpisodeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    validateRemainEpisodeRange(() => {
      onChange({ ...bundle, minRemainEpisodesNumber: Number(e.target.value) });
    });
  };

  const handleMaxRemainEpisodeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    validateRemainEpisodeRange(() => {
      onChange({ ...bundle, maxRemainEpisodesNumber: Number(e.target.value) });
    });
  };

  const renderBundleId = (id: number): JSX.Element => (
    <Tooltip title="Bundle Id">
      <Chip size="small" label={id} color="primary" className={classes.bundleIdChip} />
    </Tooltip>
  );

  return (
    <Paper className={classes.item}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        {!isDynamicBundle && (
          <Box display="flex" alignItems="center" paddingBottom={1}>
            {!!bundle.id && renderBundleId(bundle.id)}
            <Typography>{title}</Typography>
          </Box>
        )}
        {isDynamicBundle && (
          <Box display="flex" alignItems="center" justifyContent="flex-start" paddingBottom={1}>
            {!!bundle.id && renderBundleId(bundle.id)}
            <Typography variant="subtitle2" className={classes.remainEpisodeTitle}>
              Remaining Episodes
            </Typography>
            {!isEditing && <Typography>{`${bundle.minRemainEpisodesNumber} ~ ${bundle.maxRemainEpisodesNumber}`}</Typography>}
            {isEditing && (
              <React.Fragment>
                <TextField
                  value={minEpisode}
                  type="number"
                  label="≥"
                  variant="outlined"
                  size="small"
                  error={!!errorMessage}
                  inputProps={{
                    min: 1,
                  }}
                  onChange={e => setMinEpisode(e.target.value)}
                  className={classes.remainEpisodeTextField}
                  onBlur={handleMinRemainEpisodeBlur}
                />
                <Typography>~</Typography>
                <TextField
                  value={maxEpisode}
                  type="number"
                  label="≤"
                  variant="outlined"
                  size="small"
                  error={!!errorMessage}
                  inputProps={{
                    min: 1,
                  }}
                  onChange={e => setMaxEpisode(e.target.value)}
                  className={classes.remainEpisodeTextField}
                  onBlur={handleMaxRemainEpisodeBlur}
                />
                {errorMessage && (
                  <Typography className={classes.errorMessage} variant="caption">
                    {errorMessage}
                  </Typography>
                )}
              </React.Fragment>
            )}
          </Box>
        )}

        <Box display="flex" alignItems="center" justifyContent="flex-end">
          {isEditing && !isDynamicBundle && (
            <IconButton onClick={handleAddDynamicBundleClick}>
              <Tooltip title="Add dynamic bundles based on the remaining episodes of the user." arrow enterDelay={500} placement="left">
                <AddIcon />
              </Tooltip>
            </IconButton>
          )}

          {removable && (
            <IconButton onClick={handleRemoveClick}>
              <CloseIcon style={{ color: '#bebebe' }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Grid container direction="column">
        {Array.from({ length: getLengthOfTiers() }).map((_, i) => {
          const tier = prices[i];
          const previousTier = i > 0 ? prices[i - 1] : undefined;

          return (
            <Grid container key={i} alignItems="center" className={classes.tier}>
              <Grid item xs={4}>
                <Typography>Tier {i + 1}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Box display="flex" alignItems="center">
                  {isEditing ? (
                    <Select
                      value={tier?.episodeNum || ''}
                      disabled={previousTier && !previousTier.episodeNum}
                      onChange={e => {
                        const value = Number(e.target.value);
                        const newPrice = [...prices];
                        newPrice[i] = { ...tier, price: 0, episodeNum: value };

                        if (newPrice[i + 1] && newPrice[i + 1].episodeNum <= value) {
                          for (let idx = i + 1; idx < newPrice.length; idx++) {
                            newPrice[idx] = { price: 0, episodeNum: 0 };
                          }
                        }

                        onChange({ ...bundle, prices: newPrice });
                      }}
                    >
                      {renderEpisodeCountItems(previousTier)}
                    </Select>
                  ) : (
                    <Typography variant="subtitle2" className={classes.number}>
                      {tier?.episodeNum || '--'}
                    </Typography>
                  )}

                  <Typography>Episodes</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box display="flex" alignItems="center">
                  {isEditing ? (
                    <Select
                      value={tier?.price || ''}
                      disabled={!tier?.episodeNum || (previousTier && !previousTier?.price)}
                      onChange={e => {
                        const value = Number(e.target.value);
                        const newPrice = [...prices];
                        newPrice[i] = { ...tier, price: value };

                        for (let idx = i + 1; idx < newPrice.length; idx++) {
                          newPrice[idx] = { ...newPrice[idx], price: 0 };
                        }

                        onChange({ ...bundle, prices: newPrice });
                      }}
                    >
                      {renderCalculatedCoinItems(tier?.episodeNum, previousTier)}
                    </Select>
                  ) : (
                    <Typography variant="subtitle2" className={classes.number}>
                      {tier && tier?.price ? `${tier.price} (${getRatioToPercentage(tier.price / (tier.episodeNum * episodeUnitPrice))}% off)` : '--'}
                    </Typography>
                  )}
                  <Typography>Coins</Typography>
                </Box>
              </Grid>
            </Grid>
          );
        })}
        <Grid item>
          <Box display="flex" alignItems="center">
            <Checkbox checked={showRemainAll} onChange={handleCheckboxChange} disabled={!isEditing} />
            <Typography>Display all remaining episodes</Typography>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SingleBundle;
